import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {assertUnreachable} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerFooterActions} from '../../../../../libs/shared/ui/src/components/banners/bannerFooterActions';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {BannerText} from '../../../../../libs/shared/ui/src/components/banners/bannerText';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {hideBannerAction} from '../../../../store/panels/app/banners/bannersActions';
import {
  PortalSuccessBannerState,
  PortalSuccessBannerTypesEnum,
} from '../../../../store/panels/app/banners/bannersState';

/*
 * Props.
 */

interface PortalSuccessBannerProps {
  state: PortalSuccessBannerState;
}

/*
 * Component.
 */

export const PortalSuccessBanner: FC<PortalSuccessBannerProps> = ({state}) => {
  const shouldLinkToKb = Boolean(state.knowledgeBaseId);
  const theme = useUserTheme();
  const dispatch = useAppDispatch();
  const {navigateToLink} = useLinkNavigation();

  const onNavigateToKnowledgeBase = () => {
    if (!state.knowledgeBaseId) {
      return;
    }
    navigateToLink(`/knowledge-base/${state.knowledgeBaseId}/settings/customer-portal`);
    dispatch(hideBannerAction(state.uid));
  };

  return (
    <Banner
      bannerType={shouldLinkToKb ? BannerTypesEnum.MULTILINE : BannerTypesEnum.INLINE}
      priority={BannerPrioritiesEnum.INFORMATION}
      color={theme.palette.green.shade40}
    >
      <BannerCloseAction onClick={() => dispatch(hideBannerAction(state.uid))} />
      <BannerIcon iconName="checkmarkCircleSmall" />
      {renderBannerContent(state, onNavigateToKnowledgeBase)}
    </Banner>
  );
};

/*
 * Helpers.
 */

function renderBannerContent(state: PortalSuccessBannerState, onNavigateToKnowledgeBase: () => void) {
  switch (state.portalSuccessType) {
    case PortalSuccessBannerTypesEnum.PORTAL_CREATED:
      return (
        <>
          <BannerText>
            <Trans id="portal-sucess-banner-channel-connected">Channel connected</Trans>
          </BannerText>
          {state.knowledgeBaseId && (
            <BannerFooterActions>
              <BannerAction onClick={onNavigateToKnowledgeBase}>
                <Trans id="portal-sucess-banner-channel-connected">Back to knowledge base</Trans>
              </BannerAction>
            </BannerFooterActions>
          )}
        </>
      );
    default:
      assertUnreachable(state.portalSuccessType);
      return null;
  }
}
